import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import { alpha, styled } from "@mui/material/styles";
import { ReactNode, useCallback } from "react";

import { useBalance, useGameMoney, useIsLoggedIn, useShownInput, useTranslate } from "@/hooks";
import { colors } from "@/theme";

const QuickRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: theme.spacing(0.75),
  marginTop: theme.spacing(1),
}));

const QuickPick = styled("button")(({ theme }) => ({
  minWidth: 0,
  minHeight: 28,
  padding: theme.spacing(0, 0.5),
  borderRadius: 999,
  border: "1px solid",
  borderColor: theme.palette.divider,
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: 12,
  fontWeight: 700,
  fontVariantNumeric: "tabular-nums",
  backgroundColor: theme.vars.palette.game.track,
  color: theme.palette.text.secondary,
  "&:hover:not(:disabled)": {
    borderColor: alpha(colors.prime, 0.55),
    color: theme.palette.text.primary,
  },
  "&:focus-visible": {
    outline: `2px solid ${alpha(colors.prime, 0.8)}`,
    outlineOffset: 2,
  },
  "&:disabled": { cursor: "default", opacity: 0.4 },
}));

const round2 = (value: number) => Math.round(value * 100) / 100;

/** What the player can stake right now: real balance plus bonus funds, or null when unknown. */
const useAvailableFunds = (): number | null => {
  const isLoggedIn = useIsLoggedIn();
  const balance = useBalance();
  if (!isLoggedIn || !balance) return null;
  return round2((balance.balance ?? 0) + (balance.bonus ?? 0));
};

interface StakeLimits {
  min: number;
  max: number;
  /** Replaces the generic "maximum bet" message when a game has its own ceiling. */
  maxMessage?: string;
}

/** Why `amount` cannot be staked, or null; checks the balance so a bet is refused before the request. */
export const useStakeProblem = (amount: string, { min, max, maxMessage }: StakeLimits) => {
  const t = useTranslate();
  const { fmt } = useGameMoney();
  const funds = useAvailableFunds();
  const stake = Number(amount);

  if (amount.trim() === "" || !Number.isFinite(stake) || stake <= 0) {
    return t("game.shared.bet.not-an-amount");
  }
  if (stake < min) return t("game.shared.bet.min-bet", { min: fmt(min) });
  if (stake > max) return maxMessage ?? t("game.shared.bet.max-bet", { max: fmt(max) });
  if (funds !== null && stake > funds) {
    return t("game.shared.bet.insufficient", { balance: fmt(funds) });
  }
  return null;
};

export interface StakeFieldProps extends StakeLimits {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label?: string;
  /** Shown under the field while the stake is fine; defaults to the table's min and max. */
  helperText?: ReactNode;
}

/** The stake input every original shares: ½ / 2× / Max and an inline reason when it cannot be bet. */
const StakeField = ({
  value,
  onChange,
  min,
  max,
  maxMessage,
  disabled,
  label,
  helperText,
}: StakeFieldProps) => {
  const t = useTranslate();
  const { code, fmt } = useGameMoney();
  // The game keeps `value` in USD; the player types the show currency.
  const input = useShownInput(value, onChange);
  const funds = useAvailableFunds();
  const problem = useStakeProblem(value, { min, max, maxMessage });
  const stake = Number(value);
  const current = Number.isFinite(stake) ? stake : 0;

  // The steps clamp rather than refuse: doubling past the maximum means "the maximum".
  const setStake = useCallback(
    (next: number) => onChange(Math.min(max, Math.max(min, round2(Math.max(0, next)))).toFixed(2)),
    [onChange, min, max],
  );

  const showProblem = value.trim() !== "" && !!problem;

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        label={label ?? t("game.shared.bet.amount")}
        value={input.value}
        onChange={(event) => input.onChange(event.target.value)}
        disabled={disabled}
        error={showProblem}
        type="number"
        inputProps={{
          min: Number(fmt(min)),
          max: Number(fmt(max)),
          step: 0.1,
          inputMode: "decimal",
        }}
        InputProps={{
          endAdornment: code ? <InputAdornment position="end">{code}</InputAdornment> : null,
        }}
        helperText={
          showProblem
            ? problem
            : (helperText ?? t("game.shared.bet.min-max", { min: fmt(min), max: fmt(max) }))
        }
      />
      <QuickRow>
        <QuickPick
          type="button"
          disabled={disabled}
          onClick={() => setStake(current / 2)}
          aria-label={t("game.shared.bet.halve")}
        >
          ½
        </QuickPick>
        <QuickPick
          type="button"
          disabled={disabled}
          onClick={() => setStake(current * 2)}
          aria-label={t("game.shared.bet.double")}
        >
          2×
        </QuickPick>
        <QuickPick
          type="button"
          disabled={disabled}
          onClick={() =>
            setStake(funds === null ? max : Math.floor(Math.min(max, funds) * 100) / 100)
          }
          aria-label={t("game.shared.bet.stake-max")}
        >
          {t("max")}
        </QuickPick>
      </QuickRow>
    </Box>
  );
};

export default StakeField;
